import { FC, PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { StoreApi, createStore, useStore } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import {
  Range,
  Upload,
  UploadProviderOptions,
  UploadSession,
  UploadStatus,
  queuedUploads,
  readFileInChunks
} from './utils';

type WritableDraft<T> = T;

/*
  "James", I hear you ask, "Why have you made such a ridiculously overcomplicated upload provider?"
  The goal of this is two fold. Firstly, to provide a great user experience with reliable uploads of large files,
  uploading in chunks and resuming those uploads if they fail for any reason. Secondly, to decouple the backend storage mechanism
  from the upload service that the UI will consume. My home is that we can use this not just for OneDrive but also blob storage, etc.
*/

interface UploadProviderInfo {
  sessions: UploadSession[];
  uploadStarted?: Date;
  uploadError?: string;
}

interface UploadProviderActions {
  uploadFiles(files: File[] | FileList, parentId?: string): Promise<void>;
  cancelUploadSession(id: string): Promise<void>;
  hideUploadSession(id: string): void;

  retryUploadSession(id: string, file: File): Promise<void>;
  retryUploadSessions(files: File[] | FileList): Promise<void>;
  identifyInterruptedUploads(): void;
}

interface UploadProviderProps {
  createUploadSession(name: string, file: File, parentId?: string): Promise<UploadSession>;
  uploadChunk(session: UploadSession, chunk: ArrayBuffer, range: Range): Promise<void>;
  uploadFile(session: UploadSession, file: File): Promise<void>;
  completeUpload(session: UploadSession): Promise<void>;
  destroyUpload(session: UploadSession): Promise<void>;
  options: UploadProviderOptions;
}

// These are immer actions, so we can mutate the draft directly
// We can do this inline as we've done in some of the service but it's often easier to read if we extract them out

const changeStatus =
  (id: string, status: UploadStatus) => (draft: WritableDraft<UploadProviderInfo & UploadProviderActions>) => {
    const session = draft.sessions.find(s => s.id === id);
    if (!session) return;
    session.status = status;
  };

const updateProgress =
  (id: string, range: Range, duration: number) =>
  (draft: WritableDraft<UploadProviderInfo & UploadProviderActions>) => {
    const session = draft.sessions.find(s => s.id === id);
    if (!session) return;
    if (!session.progress) session.progress = [];
    session.progress.push({ range, duration });
  };

const updateResult =
  (id: string, result: any) => (draft: WritableDraft<UploadProviderInfo & UploadProviderActions>) => {
    const session = draft.sessions.find(s => s.id === id);
    if (!session) return;
    session.result = result;
  };

const addSession = (session: UploadSession) => (draft: WritableDraft<UploadProviderInfo & UploadProviderActions>) => {
  draft.sessions.push(session);
};

// Defining this once here means we don't have to do it in every serialise call that uses this
// Regex are expensive to create so we want to avoid that
const IsSerialisedDate = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
const UploadProviderContext = createContext<StoreApi<UploadProviderInfo & UploadProviderActions> | null>(null);

export const UploadProvider: FC<PropsWithChildren<UploadProviderProps>> = ({
  createUploadSession,
  uploadChunk,
  uploadFile,
  completeUpload,
  destroyUpload,
  options,
  children
}) => {
  const store = useMemo(
    () =>
      createStore(
        persist(
          immer<UploadProviderInfo & UploadProviderActions>((set, get) => ({
            sessions: [],
            uploadFiles: async (files, parentId?: string) => {
              // Remember when the upload started
              set(state => {
                state.uploadStarted = new Date();
                state.uploadError = undefined;
              });

              // We want to work in arrays, not FileLists but that is what we get from the input element
              // so to be nicer to the user we convert it here.
              files = Array.from(files);

              // Create a session for each file
              const uploads = await Promise.all(
                files.map<Promise<Upload>>(async file => {
                  try {
                    // Create a session for the file
                    const session = await createUploadSession(file.name, file, parentId);
                    // Add the session to the store
                    set(addSession(session));
                    // Return the session and the file
                    return [session, file];
                  } catch (e) {
                    console.log(e);
                    set({ uploadError: 'Could not create upload session' });
                    throw Error('Could not create upload sessions.');
                  }
                })
              );

              // Sort the uploads by session created date
              // This is just as a convenience to the user so they can see the uploads in the order they were added
              // Otherwise it just seems a bit random
              uploads.sort(([a], [b]) => a.created.getTime() - b.created.getTime());

              // Upload the files. We do this sequentially but we could do it in parallel in the future.
              for (const [session, file] of queuedUploads(uploads)) {
                try {
                  // If the file is small enough we can upload it in one go
                  if (file.size <= options.chunkedUploadThreshold) {
                    set(changeStatus(session.id, UploadStatus.Uploading));
                    await uploadFile(session, file);
                    const result = await completeUpload(session);
                    set(updateResult(session.id, result));
                    set(changeStatus(session.id, UploadStatus.Complete));

                    continue;
                  }

                  // Otherwise we need to upload it in chunks
                  set(changeStatus(session.id, UploadStatus.Uploading));
                  for await (const chunk of readFileInChunks(file, { chunkSize: options.chunkSize })) {
                    // Check for abort
                    const { sessions } = get();
                    const current = sessions.find(s => s.id === session.id);
                    if (!current || current.status === UploadStatus.Cancelled) break;

                    // Upload the chunk
                    const range = { start: chunk.offset, end: chunk.offset + chunk.data.byteLength - 1 };
                    const start = Date.now();
                    await uploadChunk(session, chunk.data, range);
                    const duration = Date.now() - start;
                    set(updateProgress(session.id, range, duration));
                  }
                  const result = await completeUpload(session);
                  set(updateResult(session.id, result));
                  set(changeStatus(session.id, UploadStatus.Complete));
                } catch (e) {
                  console.error(e);
                  set(changeStatus(session.id, UploadStatus.Failed));
                  set({ uploadError: 'Unable to complete upload' });
                }
              }
            },
            cancelUploadSession: async id => {
              const { sessions } = get();
              const session = sessions.find(s => s.id === id);
              if (!session) return;
              if (session.status === UploadStatus.Complete || session.status === UploadStatus.Cancelled) return;
              set(changeStatus(session.id, UploadStatus.Cancelled));
              await destroyUpload(session);
            },
            hideUploadSession: id => {
              set(state => {
                state.sessions = state.sessions.filter(s => s.id !== id);
              });
            },
            retryUploadSession: async (id, file) => {
              set(state => {
                state.uploadError = undefined;
              });

              const { sessions } = get();
              const session = sessions.find(s => s.id === id);
              if (!session) return;
              if (
                session.status === UploadStatus.Complete ||
                session.status === UploadStatus.Uploading ||
                session.status === UploadStatus.Cancelled
              ) {
                return;
              }

              // Perform the upload
              if (file.size <= options.chunkedUploadThreshold) {
                if (session.status !== UploadStatus.Waiting) {
                  throw new Error('Not implemented: retrying non-chunked, non-waiting uploads');
                }

                set(changeStatus(session.id, UploadStatus.Uploading));
                await uploadFile(session, file);
                const result = await completeUpload(session);
                set(updateResult(session.id, result));
                set(changeStatus(session.id, UploadStatus.Complete));

                return;
              }

              set(changeStatus(session.id, UploadStatus.Uploading));
              const progress = session.progress?.at(-1);
              const start = progress ? progress.range.end + 1 : 0;

              for await (const chunk of readFileInChunks(file, {
                chunkSize: options.chunkSize,
                initialOffset: start
              })) {
                // Check for abort
                const { sessions } = get();
                const current = sessions.find(s => s.id === session.id);
                if (!current || current.status === UploadStatus.Cancelled) break;

                // Upload the chunk
                const range = { start: chunk.offset, end: chunk.offset + chunk.data.byteLength - 1 };
                const start = Date.now();
                await uploadChunk(session, chunk.data, range);
                const duration = Date.now() - start;
                set(updateProgress(session.id, range, duration));
              }
              const result = await completeUpload(session);
              set(updateResult(session.id, result));
              set(changeStatus(session.id, UploadStatus.Complete));
            },
            identifyInterruptedUploads: () => {
              const { sessions } = get();
              sessions.forEach(session => {
                if (session.status === UploadStatus.Complete || session.status === UploadStatus.Cancelled) return;
                set(changeStatus(session.id, UploadStatus.Interrupted));
              });
            },
            retryUploadSessions: async list => {
              const files = Array.from(list);
              // Try and match incoming file to existing interrupted uploads
              const { sessions, retryUploadSession } = get();
              //
              const retryableSessions = sessions.filter(session => {
                // If the session is not interrupted then we can't retry it
                if (session.status !== UploadStatus.Interrupted) return false;
                // If we can't find a file that matches the session then we can't retry it
                const file = files.find(f => f.name === session.name && f.size === session.size);
                return !!file;
              });
              // Set the status of the sessions to waiting
              retryableSessions.map(session => set(changeStatus(session.id, UploadStatus.Waiting)));
              // Sort the sessions by created date
              const sortedSessions = retryableSessions.sort((a, b) => a.created.getTime() - b.created.getTime());
              for (const session of sortedSessions) {
                const file = files.find(f => f.name === session.name && f.size === session.size);
                if (file) {
                  await retryUploadSession(session.id, file);
                }
              }
            }
          })),
          {
            name: 'upload',
            storage: createJSONStorage(() => localStorage, {
              reviver: (key, value) =>
                // This is a ridiculously inefficient way of serialising dates but I didn't want to forget to specific a key in the future and everything break.
                // As long as we don't have an insane number of uploads this should be fine.
                typeof value === 'string' && IsSerialisedDate.test(value) ? new Date(value) : value
            }),      
            partialize: (state) =>
            Object.fromEntries(
              Object.entries(state).filter(([key]) => !['uploadError'].includes(key)),
            ),
            version: 1
          }
        )
      ),
    [createUploadSession, uploadChunk, uploadFile, completeUpload, options]
  );

  return (
    <UploadProviderContext.Provider value={store}>
      <UploadManager />
      {children}
    </UploadProviderContext.Provider>
  );
};

enum MigrationState {
  Unknown,
  Migrated
}

export const UploadManager = () => {
  // We need to identify any interrupted uploads and set them to the correct state
  // This is done on mount and then never again

  const identifyInterruptedUploads = useIdentifyInterruptedUploads();
  const [migrated, setMigrationState] = useState<MigrationState>(MigrationState.Unknown);

  useEffect(() => {
    if (migrated === MigrationState.Migrated) return;
    identifyInterruptedUploads();
    setMigrationState(MigrationState.Migrated);
  }, [identifyInterruptedUploads, setMigrationState]);

  return null;
};

type SelectorReturn<S extends (s: UploadProviderInfo & UploadProviderActions) => any> = ReturnType<S>;

export function useUpload<S extends (s: UploadProviderInfo & UploadProviderActions) => any>(
  selector: S
): SelectorReturn<S> {
  const context = useContext(UploadProviderContext);
  if (!context) {
    throw new Error('useUpload must be used within a UploadProvider');
  }
  return useStore(context, selector);
}

export const useUploadFiles = () => {
  return useUpload(s => s.uploadFiles);
};

export const useRetryUploadSessions = () => {
  return useUpload(s => s.retryUploadSessions);
};

export const useIdentifyInterruptedUploads = () => {
  return useUpload(s => s.identifyInterruptedUploads);
};

export function useUploadSessions<S extends UploadSession>() {
  const sessions = useUpload(s => s.sessions);
  return sessions as S[];
}

export function useLastUpload() {
  return useUpload(s => s.uploadStarted);
}

export function useUploadSession<S extends UploadSession>(id: string) {
  const selector = useCallback(
    (s: UploadProviderInfo & UploadProviderActions) => {
      return [
        s.sessions.find(s => s.id === id),
        () => s.hideUploadSession(id),
        () => s.cancelUploadSession(id),
        (file: File) => s.retryUploadSession(id, file)
      ] as const;
    },
    [id]
  );
  const [session, hide, cancel, retry] = useUpload(selector);
  if (!session) throw new Error(`No upload session found with id ${id}`);
  return [session as S, hide, cancel, retry] as const;
}
